import type { IconName } from './iconNames';

export interface AdminSection {
  /** The admin module a super admin grants (backend utils/staff.ts ADMIN_MODULES). */
  module: string;
  to: string;
  icon: IconName;
  title: string;
  cta: string;
  description: string;
  color: string;
}

// The ONE list of admin areas: the sidebar's Admin group and the /admin page
// both render it, so the two can never drift apart. Each one is a module an
// admin may be given; keep the keys in step with backend utils/staff.ts.
export const ADMIN_SECTIONS: AdminSection[] = [
  {
    module: 'projects', to: '/admin/projects', icon: 'folder_managed', color: 'bg-purple-50 text-purple-600',
    title: 'All Projects', cta: 'Manage projects',
    description: 'Browse and manage all projects on the platform',
  },
  {
    module: 'users', to: '/admin/users', icon: 'manage_accounts', color: 'bg-blue-50 text-blue-600',
    title: 'All Users', cta: 'Manage users',
    description: 'View, manage roles and activate/deactivate users',
  },
  {
    module: 'user-plans', to: '/admin/user-plans', icon: 'workspace_premium', color: 'bg-yellow-50 text-yellow-600',
    title: 'User Plans', cta: 'Manage plans',
    description: 'Define storage and download quotas per plan tier',
  },
  {
    module: 'conversion', to: '/admin/conversion', icon: 'transform', color: 'bg-orange-50 text-orange-600',
    title: 'Conversion Jobs', cta: 'Manage jobs',
    description: 'Monitor conversion jobs, retry failures, run custom jobs',
  },
  {
    module: 'conversion-stats', to: '/admin/conversion-stats', icon: 'monitoring', color: 'bg-teal-50 text-teal-600',
    title: 'Conversion Monitor', cta: 'View statistics',
    description: 'Queue waits, runtimes, lane capacity and scaling alerts',
  },
  {
    module: 'user-activity', to: '/admin/user-activity', icon: 'insights', color: 'bg-indigo-50 text-indigo-600',
    title: 'User Activity', cta: 'View activity',
    description: 'See which features users use most and rank users by sharing, downloads, uploads',
  },
  {
    module: 'costs', to: '/admin/costs', icon: 'payments', color: 'bg-green-50 text-green-600',
    title: 'Cost Analysis', cta: 'View costs',
    description: 'What the platform costs to run, per user and file type',
  },
  {
    module: 'cost-calculator', to: '/admin/cost-calculator', icon: 'calculate', color: 'bg-emerald-50 text-emerald-600',
    title: 'Cost Calculator', cta: 'Open calculator',
    description: 'Price a workload before it arrives: uploads per month, storage, viewing and downloads',
  },
  {
    module: 'deleted-files', to: '/admin/deleted-files', icon: 'delete_sweep', color: 'bg-red-50 text-red-600',
    title: 'Deleted Files', cta: 'Review deleted files',
    description: 'Files in Trash across the platform — restore them or remove them for good',
  },
  {
    module: 'scheduled-tasks', to: '/admin/scheduled-tasks', icon: 'schedule', color: 'bg-cyan-50 text-cyan-600',
    title: 'Scheduled Tasks', cta: 'Manage tasks',
    description: 'Every background job: see when it runs, change its timing, pause it or run it now',
  },
  {
    module: 'release-notes', to: '/admin/release-notes', icon: 'campaign', color: 'bg-sky-50 text-sky-600',
    title: 'Release Notes', cta: 'Manage notes',
    description: "Publish what's new — visible to every user via the megaphone icon",
  },
  {
    module: 'settings', to: '/admin/settings', icon: 'tune', color: 'bg-slate-50 text-slate-600',
    title: 'Platform Settings', cta: 'Configure',
    description: 'Global behaviour, feature flags and safety limits',
  },
];
